import { apiJson } from "./client";

export interface Me {
  id: string;
  username: string;
  name: string;
  email: string | null;
  /** The server-derived persona: qc_lead, manager, inspector, planner or contractor. */
  workflowClass: string | null;
  /**
   * Wave S: for a counterparty account, how many contracts the operator has assigned to
   * it; zero means it reads nothing of its party yet. Null (or absent) for an operator
   * account. A server fact the empty states read; never derived on the client.
   */
  assignedContractCount?: number | null;
  /** Wave COM item 4: the account asked for no email; the in-app bell stays. */
  emailOptOut?: boolean;
}

/**
 * The authenticated user, from the API's own view of the token.
 *
 * The client deliberately does not decode the token to find out who it is signed in as.
 * The server's answer is the authoritative one, and asking for it is also the proof that
 * the token this browser holds is one the server accepts.
 *
 * At M1 this is replaced-in-kind by the generated `openapi-fetch` client over
 * `schema.d.ts` (ADR-022 section 9) when the generator lands; the shape below is the same
 * contract, written by hand until then.
 */
export async function fetchMe(accessToken: string, signal?: AbortSignal): Promise<Me> {
  const response = await fetch("/api/me", {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
    signal: signal ?? null,
  });
  if (!response.ok) {
    throw new Error(`The identity request failed with ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  const parsed = body as Record<string, unknown>;
  if (typeof parsed.id !== "string" || typeof parsed.name !== "string") {
    throw new Error("The identity response was not in the expected shape");
  }
  return {
    id: parsed.id,
    username: typeof parsed.username === "string" ? parsed.username : parsed.id,
    name: parsed.name,
    email: typeof parsed.email === "string" ? parsed.email : null,
    workflowClass: typeof parsed.workflow_class === "string" ? parsed.workflow_class : null,
    assignedContractCount:
      typeof parsed.assigned_contract_count === "number" ? parsed.assigned_contract_count : null,
    emailOptOut: parsed.email_opt_out === true,
  };
}

/**
 * Wave COM item 4: the caller's own preferences (PATCH /api/me/preferences). `changed`
 * is false when the account already read this; nothing was written then.
 */
export async function setPreferences(
  accessToken: string,
  emailOptOut: boolean,
): Promise<{ emailOptOut: boolean; changed: boolean }> {
  return apiJson(
    accessToken,
    "/me/preferences",
    (raw) => {
      const body = raw as Record<string, unknown>;
      return { emailOptOut: body.email_opt_out === true, changed: body.changed === true };
    },
    { method: "PATCH", body: { email_opt_out: emailOptOut } },
  );
}
